import React from 'react';
import { formatDateOnly, formatLocalDate, parseLocalDate } from '../utils/dateUtils';
import ClientTimeline from './ClientTimeline';
import ClientCharts from './ClientCharts';
import MeetingSummaryModal from './MeetingSummaryModal';
import ObjectionChart from './ObjectionChart';
import ServicesSoldChart from './ServicesSoldChart';
import ClientDetailsSection from './ClientDetailsSection';
import ClientTasksSection from './ClientTasksSection';
import TasksInlineSection from './TasksInlineSection';

const ClientProfile = ({ client, onBack, onNewMeeting, onEditMeeting, onUpdateClient, canEdit, currentUser }) => {
  const [showSummaryModal, setShowSummaryModal] = React.useState(false);
  const [selectedMeetingForSummary, setSelectedMeetingForSummary] = React.useState(null);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [newClientName, setNewClientName] = React.useState(client.name);
  const [showClientDetails, setShowClientDetails] = React.useState(false);
  const [showClientTasks, setShowClientTasks] = React.useState(false);

  const calculateTotalPatients = () => {
    return client.meetings.reduce((total, meeting) => total + meeting.patients, 0);
  };

  const calculateTotalContacts = () => {
    return client.meetings.reduce((total, meeting) => total + meeting.contactsGenerated, 0);
  };

  const calculateConversionRate = () => {
    const totalContacts = calculateTotalContacts();
    if (totalContacts === 0) return '0%';
    const rate = (calculateTotalPatients() / totalContacts) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const handleViewSummary = (meeting) => {
    setSelectedMeetingForSummary({ ...meeting, clientName: client.name });
    setShowSummaryModal(true);
  };

  const handleSaveClientName = () => {
    if (newClientName.trim() !== '' && newClientName !== client.name) {
      onUpdateClient({ ...client, name: newClientName.trim() });
    }
    setIsEditingName(false);
  };

  const totalPatients = calculateTotalPatients();
  const totalContacts = calculateTotalContacts();
  const conversionRate = calculateConversionRate();

  // Calcular el máximo para escalar las barras de rendimiento general
  const maxContactsPatients = Math.max(totalContacts, totalPatients);
  const contactsWidth = maxContactsPatients > 0 ? (totalContacts / maxContactsPatients) * 100 : 0;
  const patientsWidth = maxContactsPatients > 0 ? (totalPatients / maxContactsPatients) * 100 : 0;
  const conversionWidth = parseFloat(conversionRate) > 0 ? parseFloat(conversionRate) : 0;

  // Obtener la reunión más reciente basada en la fecha, no en la posición del array
  const lastMeeting = client.meetings.length > 0 
    ? client.meetings.reduce((latest, meeting) => {
        const latestDate = new Date(latest.date);
        const meetingDate = new Date(meeting.date);
        return meetingDate > latestDate ? meeting : latest;
      })
    : null;

  // Calcular valor total de servicios vendidos
  const totalServicesValue = client.meetings.reduce((total, meeting) => {
    return total + (meeting.servicesSold || []).reduce((sum, service) => sum + (service.quantity * service.value), 0);
  }, 0);

  const generateTextReport = () => {
    let reportContent = `--- REPORTE DE EXPEDIENTE DEL CLIENTE ---\n\n`;
    reportContent += `Nombre del Cliente: ${client.name}\n`;
    if (client.specialty) reportContent += `Especialidad: ${client.specialty}\n`;
    if (client.cell) reportContent += `Célula: ${client.cell}\n`;
    if (client.email) reportContent += `Email: ${client.email}\n`;
    if (client.country) reportContent += `País: ${client.country}\n\n`;

    reportContent += `--- DATOS DEL CLIENTE ---\n`;
    if (client.googleAdsLink) reportContent += `Google Ads: ${client.googleAdsLink}\n`;
    if (client.googleMapsLink) reportContent += `Google Maps: ${client.googleMapsLink}\n`;
    if (client.facebookLink) reportContent += `Facebook: ${client.facebookLink}\n`;
    if (client.metaAccountLink) reportContent += `Cuenta de Meta: ${client.metaAccountLink}\n`;
    if (client.salesExecutive) reportContent += `Ejecutivo de Ventas: ${client.salesExecutive}\n\n`;

    reportContent += `--- RENDIMIENTO GENERAL ---\n`;
    reportContent += `Total Juntas: ${client.meetings.length}\n`;
    reportContent += `Contactos Generados: ${totalContacts}\n`;
    reportContent += `Pacientes Concretados: ${totalPatients}\n`;
    reportContent += `Tasa de Conversión: ${conversionRate}\n\n`;

    reportContent += `--- HISTORIAL DE JUNTAS ---\n`;
    if (client.meetings.length === 0) {
      reportContent += `No hay juntas registradas.\n\n`;
    } else {
      client.meetings.slice().reverse().forEach((meeting, index) => {
        const contacts = meeting.contactsGenerated;
        const patients = meeting.patients;
        const conversion = contacts > 0 ? ((patients / contacts) * 100).toFixed(1) + '%' : '0%';
        const totalServicesValue = (meeting.servicesSold || []).reduce((sum, service) => sum + (service.quantity * service.value), 0);
        const totalObjections = (meeting.objections?.price || 0) + (meeting.objections?.location || 0) + (meeting.objections?.noResponse || 0) + (meeting.objections?.schedule || 0);

        reportContent += `Junta #${client.meetings.length - index} - Fecha: ${formatDateOnly(meeting.date)}\n`;
        reportContent += `  Tipo de Junta: ${meeting.meetingType || 'N/A'}\n`;
        reportContent += `  Resumen: ${meeting.observations}\n`;
        reportContent += `  Acciones: ${meeting.agreements}\n`;
        reportContent += `  Contactos: ${contacts}, Pacientes: ${patients}, Conversión: ${conversion}\n`;
        if (meeting.nextMeetingDate) reportContent += `  Próxima Junta: ${formatDateOnly(meeting.nextMeetingDate)} a las ${meeting.nextMeetingTime || 'N/A'} hrs\n`;
        if (meeting.clientMeetingEmail) reportContent += `  Email Agendamiento: ${meeting.clientMeetingEmail}\n`;
        
        if (totalObjections > 0 || meeting.objections?.other) {
          reportContent += `  Objeciones:\n`;
          if (meeting.objections.price > 0) reportContent += `    - Precio: ${meeting.objections.price} personas\n`;
          if (meeting.objections.location > 0) reportContent += `    - Ubicación: ${meeting.objections.location} personas\n`;
          if (meeting.objections.noResponse > 0) reportContent += `    - No contestan 1er mensaje: ${meeting.objections.noResponse} personas\n`;
          if (meeting.objections.schedule > 0) reportContent += `    - Horarios: ${meeting.objections.schedule} personas\n`;
          if (meeting.objections.other) reportContent += `    - Otras: ${meeting.objections.other}\n`;
        }
        if (totalServicesValue > 0) {
          reportContent += `  Servicios Vendidos:\n`;
          (meeting.servicesSold || []).forEach(service => {
            if (service.name) reportContent += `    - ${service.name}: ${service.quantity} unidades, Valor: $${service.value.toFixed(2)} c/u (Total: ${(service.quantity * service.value).toFixed(2)})\n`;
          });
          reportContent += `    Total Valor: $${totalServicesValue.toFixed(2)}\n`;
        }
        if (meeting.tasks && meeting.tasks.some(task => task.description)) {
          reportContent += `  Tareas:\n`;
          meeting.tasks.forEach(task => {
            if (task.description) {
              reportContent += `    - ${task.description}`;
              if (task.assignedTo) reportContent += ` (Asignado a: ${task.assignedTo})`;
              if (task.dueDate) reportContent += ` (Fecha límite: ${formatDateOnly(task.dueDate)})`;
              reportContent += `\n`;
            }
          });
        }
        reportContent += `\n`;
      });
    }

    reportContent += `--- ANÁLISIS DE RENDIMIENTO MENSUAL ---\n`;
    reportContent += `(Las gráficas visuales no pueden ser representadas en este formato de texto. Por favor, consulte la aplicación web para ver las gráficas de rendimiento mensual y objeciones.)\n\n`;
    reportContent += `--- FIN DEL REPORTE ---`;
    return reportContent;
  };

  const printPage = () => {
    console.log('printPage - Imprimiendo página del historial de:', client.name);
    try {
      window.print();
    } catch (error) {
      console.error('printPage - Error al imprimir:', error);
      alert('Error al imprimir la página. Por favor, inténtalo de nuevo.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header Premium con gradiente */}
        <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] rounded-2xl shadow-xl p-6 sm:p-8 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <button 
                onClick={onBack}
                className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white py-3 px-6 rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Volver</span>
              </button>
              
              <div className="text-white">
                {isEditingName ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                      className="text-xl sm:text-2xl lg:text-3xl font-bold bg-transparent border-b-2 border-white focus:outline-none text-white placeholder-blue-200"
                      placeholder="Nombre del cliente"
                    />
                    {canEdit && (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveClientName}
                          className="bg-green-500 text-white py-1 px-3 rounded-lg text-sm hover:bg-green-600 transition-colors"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingName(false);
                            setNewClientName(client.name);
                          }}
                          className="bg-red-500 text-white py-1 px-3 rounded-lg text-sm hover:bg-red-600 transition-colors"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
                      {client.name}
                    </h1>
                    {canEdit && (
                      <button
                        onClick={() => setIsEditingName(true)}
                        className="text-blue-200 hover:text-white transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
                
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  {client.specialty && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                      {client.specialty}
                    </span>
                  )}
                  {client.cell && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                      Célula: {client.cell}
                    </span>
                  )}
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    {client.meetings.length} juntas
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {/* Botón Imprimir - Solo para administradores */}
              {currentUser && currentUser.role === 'admin' && (
                <button
                  onClick={printPage}
                  className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white py-3 px-6 rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span className="font-medium hidden sm:inline">Imprimir</span>
                </button>
              )}
              
              {onNewMeeting && (
                <button
                  onClick={onNewMeeting}
                  className="group flex items-center space-x-2 bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white py-3 px-6 rounded-xl hover:from-[#8bc34a] hover:to-[#A8D08D] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="font-medium">Nueva Junta</span>
                </button>
              )}
              
              {lastMeeting && onEditMeeting && (
                <button
                  onClick={() => onEditMeeting(lastMeeting)}
                  className="group flex items-center space-x-2 bg-gradient-to-r from-[#63666A] to-[#4a4d52] text-white py-3 px-6 rounded-xl hover:from-[#4a4d52] hover:to-[#63666A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="font-medium hidden sm:inline">Editar Última</span>
                  <span className="font-medium sm:hidden">Editar</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estadísticas principales */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#0061A8] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Total Juntas</p>
                <p className="text-3xl font-bold text-[#0061A8] mt-2">{client.meetings.length}</p>
                <p className="text-xs text-[#63666A] mt-1">reuniones realizadas</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#0061A8] to-[#004d8a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#A8D08D] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Contactos Generados</p>
                <p className="text-3xl font-bold text-[#A8D08D] mt-2">{totalContacts.toLocaleString()}</p>
                <p className="text-xs text-[#63666A] mt-1">leads totales</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#63666A] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Pacientes</p>
                <p className="text-3xl font-bold text-[#63666A] mt-2">{totalPatients}</p>
                <p className="text-xs text-[#63666A] mt-1">conversiones exitosas</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#63666A] to-[#4a4d52] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#D1D3D4] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Tasa de Conversión</p>
                <p className="text-3xl font-bold text-[#D1D3D4] mt-2">{conversionRate}</p>
                <p className="text-xs text-[#63666A] mt-1">efectividad</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#D1D3D4] to-[#b8bcc0] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acciones rápidas */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Acciones Rápidas</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => setShowClientDetails(true)}
              className="group flex items-center space-x-3 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-300 border border-blue-200 hover:border-blue-300"
            >
              <div className="p-2 bg-[#0061A8] rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#0061A8]">Ver Datos del Cliente</p>
                <p className="text-sm text-[#63666A]">Información completa y enlaces</p>
              </div>
            </button>
            
            <button
              onClick={() => setShowClientTasks(true)}
              className="group flex items-center space-x-3 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl hover:from-green-100 hover:to-green-200 transition-all duration-300 border border-green-200 hover:border-green-300"
            >
              <div className="p-2 bg-[#A8D08D] rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#A8D08D]">Ver Tareas del Cliente</p>
                <p className="text-sm text-[#63666A]">Gestión de tareas y seguimiento</p>
              </div>
            </button>
          </div>
        </div>

        {/* Barras de rendimiento general */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-[#63666A] to-[#4a4d52] rounded-xl">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Rendimiento General</h3>
          </div>
          
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-[#63666A]">Contactos Generados</span>
                <span className="text-sm font-bold text-[#0061A8]">{totalContacts}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${contactsWidth}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-[#63666A]">Pacientes Concretados</span>
                <span className="text-sm font-bold text-[#A8D08D]">{totalPatients}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${patientsWidth}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-[#63666A]">Tasa de Conversión</span>
                <span className="text-sm font-bold text-[#D1D3D4]">{conversionRate}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${conversionWidth}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline de juntas */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 sm:p-8 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-[#0061A8] to-[#004d8a] rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Historial de Juntas</h3>
            </div>
          </div>
          
          <div className="p-6 sm:p-8">
            <ClientTimeline meetings={[...client.meetings].reverse()} onViewSummary={handleViewSummary} />
          </div>
        </div>

        {/* Sección de Tareas del Cliente */}
        <TasksInlineSection client={client} currentUser={currentUser} />

        {/* Gráficas y análisis */}
        {client.meetings.length > 0 && (
          <>
            <div className="mb-8">
              <ClientCharts meetings={client.meetings} />
            </div>
            <div className="mb-8">
              <ObjectionChart meetings={client.meetings} />
            </div>
            <div className="mb-8">
              <ServicesSoldChart meetings={client.meetings} />
            </div>
          </>
        )}
      </div>

      {/* Modales */}
      {showSummaryModal && selectedMeetingForSummary && (
        <MeetingSummaryModal 
          meeting={selectedMeetingForSummary}
          onClose={() => setShowSummaryModal(false)}
        />
      )}

      {showClientDetails && (
        <ClientDetailsSection 
          client={client}
          onClose={() => setShowClientDetails(false)}
          onUpdateClient={onUpdateClient}
          canEdit={canEdit}
        />
      )}

      {showClientTasks && (
        <ClientTasksSection
          client={client}
          onClose={() => setShowClientTasks(false)}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default ClientProfile;
